import { useEffect, useState } from 'react';

import type { ContentMenuPage } from '../api/types';

import { fetchMenuPages } from '../api/infoHubApi';
import { buildHubMenu, type HubMenuSection } from '../utils/contentTree';
import { HUB_SECTIONS } from '../utils/hubSections';

/**
 * The in-flight or settled page-tree request, shared by every consumer.
 *
 * The tree is one unpaged call over 1,415 rows and it backs three things at once
 * — the top bar, the landing's Knowledge centre, and the rail beside a content
 * page. Without this each of those is its own fetch, and because the rail mounts
 * with the page it would re-pull the entire tree on *every* content navigation.
 * The bar's copy already proves the data can outlive a route change, so the
 * request is cached rather than the result: a second consumer arriving mid-flight
 * joins the first one's promise instead of starting another.
 *
 * A rejection clears the cache, so a failure is retried by the next mount rather
 * than remembered for the session.
 */
let pending: Promise<ContentMenuPage[]> | null = null;

const loadMenuPages = (): Promise<ContentMenuPage[]> => {
  if (!pending) {
    pending = fetchMenuPages().catch((error: unknown) => {
      pending = null;
      throw error;
    });
  }
  return pending;
};

export interface InfoHubMenuState {
  /** The hub's top-level sections, each with whatever the data puts beneath it. */
  sections: HubMenuSection[];
  /** True until the first load settles. The menu ignores this; the landing page uses it. */
  loading: boolean;
}

/** The sections with no children yet — so the bar is the right size on first paint. */
const EMPTY_SECTIONS: HubMenuSection[] = HUB_SECTIONS.map((section) => ({
  key: section.key,
  label: section.label,
  to: section.slug,
  children: [],
  pageCount: 0,
}));

/**
 * Loads the info hub's navigation once on mount.
 *
 * The top level is fixed (see `hubSections.ts`) and everything beneath it is
 * data-driven, so a page an admin adds under a section appears in its dropdown
 * with no code change. Because the sections are known ahead of the fetch, the
 * bar renders at its final width immediately and fills in — it never reflows
 * from empty, and the overflow split is measured once rather than twice.
 *
 * A failure yields sections with no children rather than breaking the shell:
 * the nav still stands and the page the member came for still renders.
 */
export const useInfoHubMenu = (): InfoHubMenuState => {
  const [sections, setSections] = useState<HubMenuSection[]>(EMPTY_SECTIONS);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const load = async (): Promise<void> => {
      try {
        const pages = await loadMenuPages();
        if (!cancelled) {
          setSections(buildHubMenu(pages));
        }
      } catch (error) {
        console.error('Error loading info hub menu:', error);
        if (!cancelled) {
          setSections(EMPTY_SECTIONS);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();
    return (): void => {
      cancelled = true;
    };
  }, []);

  return { sections, loading };
};
